import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { ModalSelecionarCanalComponent } from '../modais/modal-selecionar-canal/modal-selecionar-canal.component';
import { ModalSelecionarEtapaComponent } from '../modais/modal-selecionar-etapa.component';
import { EnvioMensagemTemplate } from '../model/chat/mensagens/envio-mensagem-template.model';
import { TemplateBusca } from '../model/template/template-busca.model';
import { TemplateComponenteExemploModel } from '../model/template/template-componente-exemplo-model';
import { TemplateEnvioRedirecionamento } from '../model/template/template-envio-redirecionamento.model';
import { AuthService } from './auth.service';
import { CanalService } from './canal.service';
import { MsgService } from './msg.service';
import { TemplateMensagemService } from './template-mensagem.service';

@Injectable({ providedIn: 'root' })
export class EnvioTemplateChatService {

  private readonly msgService = inject(MsgService);
  private readonly dialog = inject(MatDialog);
  private readonly templateService = inject(TemplateMensagemService);
  private readonly canalService = inject(CanalService);
  private readonly authService = inject(AuthService);

  montarEnvio(template: TemplateBusca, contato: string, nomeContato: string): EnvioMensagemTemplate {
    const header = template?.components?.find(c => c.type === 'HEADER');
    const body = template?.components?.find(c => c.type === 'BODY');
    const footer = template?.components?.find(c => c.type === 'FOOTER');
    const buttons = template?.components?.find(c => c.type === 'BUTTONS');

    const envio = new EnvioMensagemTemplate(0, '', '', '', '', '', '', '', '');
    envio.templateId = template?.id ?? 0;
    envio.templateNome = template?.name || '';
    envio.textoCorpo = body?.text || '';
    envio.textoCabecalho = header?.text || '';
    envio.textoRodape = footer?.text || '';
    envio.idioma = template?.language || '';
    envio.contato = contato || '';
    envio.nomeContato = nomeContato || '';
    envio.urlImagem = Array.isArray(header?.example) ? this.montarUrlImagemHeader(header.example[0]) : '';
    envio.variaveis = body?.example?.length
      ? body.example.filter(e => e.ordem !== null).map(e => ({ ordem: e.ordem as number, valor: '' }))
      : [];
    envio.botoes = buttons?.buttons?.length
      ? buttons.buttons.map(b => ({
          titulo: b?.text || '',
          valor: b?.payload ?? b?.phone_number ?? b?.url ?? '',
          tipo: b.type || '',
        }))
      : [];
    return envio;
  }

  async preparar(envio: EnvioMensagemTemplate): Promise<boolean> {
    const usuario = this.authService.getUsuarioLogadoClariBot();
    let canalId: number | undefined;

    const atendeCanal = !!(usuario.isOperador || usuario.isCoordenador);
    if (!(await this.resolverRedirecionamento(envio, usuario.id, atendeCanal))) return false;

    if (atendeCanal) {
      const canal = await this.resolverCanal();
      if (canal === null) return false;
      canalId = canal;
    }

    try {
      if (!envio.canalId) {
        envio.canalId = canalId;
      }
      if (!envio.fluxoId) {
        const infoTemplate = await firstValueFrom(this.templateService.buscarTemplatePorId(envio.templateId));
        envio.fluxoId = infoTemplate.conta?.fluxo?.id;
      }
      return true;
    } catch {
      this.msgService.msgErro('Erro ao preparar o envio do template!');
      return false;
    }
  }

  async confirmar(envio: EnvioMensagemTemplate): Promise<boolean> {
    try {
      await firstValueFrom(this.templateService.enviarMensagemTemplate(envio));
      this.msgService.msgSucesso('Template enviado com sucesso!');
      return true;
    } catch (erro) {
      const resposta = erro as HttpErrorResponse;
      if (resposta?.status === 400) this.msgService.msgErro(resposta.error?.message ?? 'Não foi possível enviar a mensagem.');
      return false;
    }
  }

  private async resolverRedirecionamento(envio: EnvioMensagemTemplate, operadorId: number | undefined, exigeEtapa: boolean): Promise<boolean> {
    try {
      const objEnvio = new TemplateEnvioRedirecionamento();
      objEnvio.operadorId = operadorId;
      objEnvio.templateId = envio.templateId;

      const res = await firstValueFrom(this.templateService.resolverRedirecionamento(objEnvio));
      if (!res) return true;

      const etapas = res.etapas || [];
      if (etapas.length === 0 && res.fluxo.id && exigeEtapa) {
        this.msgService.msgAdvertencia('Você não pode enviar esse template pois não existe nenhuma etapa válida para seu canal');
        return false;
      }

      if (etapas.length === 1) {
        envio.fluxoId = res.fluxo.id;
        envio.canalId = etapas[0].canal?.id;
        envio.etapaId = etapas[0].id;
        return true;
      }

      if (etapas.length > 1) {
        const etapasComTitulo = etapas.map(e => ({ ...e, tituloGenerico: `${e.id}-${e.titulo}` }));
        const etapaSelecionada = await firstValueFrom(
          this.dialog.open(ModalSelecionarEtapaComponent, {
            data: { etapas: etapasComTitulo, tituloFluxo: res.fluxo.nome },
            disableClose: false,
          }).afterClosed()
        );
        if (!etapaSelecionada) return false;

        envio.fluxoId = res.fluxo.id;
        envio.canalId = etapaSelecionada.canal?.id;
        envio.etapaId = etapaSelecionada.id;
      }
      return true;
    } catch {
      this.msgService.msgErro('Erro ao resolver redirecionamento.');
      return false;
    }
  }

  private async resolverCanal(): Promise<number | null> {
    try {
      const canais = await firstValueFrom(this.canalService.buscarCanaisVinculadosAoUsuario());
      if (canais.length === 0) {
        this.msgService.msgAdvertencia('Você precisa possuir um canal vinculado para enviar essa mensagem.');
        return null;
      }
      if (canais.length === 1) {
        return canais[0].id ?? null;
      }

      const canalSelecionado = await firstValueFrom(
        this.dialog.open(ModalSelecionarCanalComponent, { data: { canais } }).afterClosed()
      );
      return canalSelecionado?.id ?? null;
    } catch {
      this.msgService.msgErro('Erro ao buscar canais vinculados ao usuário.');
      return null;
    }
  }

  private montarUrlImagemHeader(exemplo?: TemplateComponenteExemploModel): string {
    if (exemplo?.imageUuid && exemplo?.imageExtensao) {
      const extensao = exemplo.imageExtensao.replace(/^\.+/, '');
      const grupo = this.authService.getUsuarioLogadoClariBot()?.grupo;
      return `${environment.urlRepositorioClaribot}/${grupo}/templates-anexo/${exemplo.imageUuid}.${extensao}`;
    }
    return exemplo?.urlImagem ?? '';
  }
}
